import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getActiveSubscribeCampaign } from '../../config/campaigns';
import {
  DEFAULT_THX_HEADER,
  DEFAULT_THX_MESSAGE,
  DEFAULT_THX_MESSAGE_SUFFIX,
  DEFAULT_BACKGROUND_IMAGE,
  DEFAULT_MOBILE_IMAGE,
} from '../../config/defaults';
import { useSyneriseTracking } from '../shared/useSyneriseTracking';
import { pushSubscribeSuccessEvent } from '../shared/GtmDataLayerPush';
import { submitSyneriseFallback } from '../../api/subscribeApi';
import Header from '../shared/Header';
import NotFoundPage from '../shared/NotFoundPage';
import type { SubscribeSuccessState } from './types';
import './SubscribeThankYouPage.scss';

/**
 * Real /:slug/dziekujemy route — mirrors subscribe_base.html.twig's typ.html.twig exactly
 * (header + hero image + optional diagonal decor stay in place, same as the form page), not a
 * modal. Reached via navigate(..., {state}) from useSubscribeSubmit.ts; a direct visit/refresh
 * (no state) still renders the same page, just without personalization/tracking.
 */
export default function SubscribeThankYouPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const config = getActiveSubscribeCampaign(slug);
  const location = useLocation();
  const state = location.state as SubscribeSuccessState | undefined;

  useEffect(() => {
    if (state) pushSubscribeSuccessEvent(slug, state.leadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, !!state]);

  useSyneriseTracking(state, () => {
    if (state) {
      submitSyneriseFallback({ slug, ...state }).catch(() => {
        /* best-effort, mirrors legacy behavior of swallowing Synerise failures */
      });
    }
  });

  if (!config) {
    return <NotFoundPage />;
  }

  const message = (
    <div className="form-copy">
      <h1>{config.thxHeader || DEFAULT_THX_HEADER}</h1>
      <p>
        {config.thxMessage || DEFAULT_THX_MESSAGE}
        {!config.thxMessage && ` ${DEFAULT_THX_MESSAGE_SUFFIX}`}
      </p>
    </div>
  );

  // TY page always shows the shared default hero graphic, regardless of the campaign's own
  // form/error backgroundImage/mobileImage — per client request for a unified thank-you visual.
  const images = (
    <>
      <img src={DEFAULT_BACKGROUND_IMAGE} className="background-image full-height cover" alt="" />
      <img src={DEFAULT_MOBILE_IMAGE} className="background-image-mobile full-height cover" alt="" />
    </>
  );

  if (config.template === 'form-old-layout') {
    return (
      <>
        <Header />
        <div className="main-container">
          <div className="form-outer-container form-outer-container--message">
            <div className="form-bg">
              <div className="form-container">
                <div className="form-row">{message}</div>
              </div>
            </div>
          </div>
          {images}
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-container row-layout-template thx-page">
        <div className="form-outer-container form-outer-container--message">
          <div className="form-bg">
            <div className="form-container">
              <div className="form-triangle-decor" />
              <div className="form-row">{message}</div>
            </div>
          </div>
        </div>
        <div className="main-image main-image--thx">{images}</div>
      </div>
    </>
  );
}
